import React, { useEffect, useState } from 'react';
import api from '../api/axiosConfig';
import './Sidebar.css';
import { useLanguage } from '../i18n/LanguageContext';

export type SidebarView = 'notes' | 'documents' | 'discover' | 'storyboards' | 'shared'
  | 'tags' | 'settings' | 'about' | 'feedback';

interface SidebarProps {
  onAddNoteClick: () => void;
  currentView: SidebarView;
  onViewChange: (view: SidebarView) => void;
  onImportClick?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onAddNoteClick, currentView, onViewChange, onImportClick }) => {
  const { t } = useLanguage();
  // Free accounts get a gentle upgrade path right where they work
  const [isPremium, setIsPremium] = useState(true); // assume premium until known — no flash
  useEffect(() => {
    let cancelled = false;
    api.get('/billing/status/')
      .then(res => { if (!cancelled) setIsPremium(!!res.data.premium); })
      .catch(() => { /* leave as premium: never nag on errors */ });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <div className="sidebar-logo">
          <div className="logo-box-sidebar">
            <img src="/logo192.png" alt="Yarnboard" className="brand-logo-img" />
          </div>
          <h1 className="app-title">Yarnboard</h1>
        </div>
      </div>
      
      <nav className="sidebar-nav">
        <button 
          className={`nav-item ${currentView === 'notes' ? 'active' : ''}`}
          onClick={() => onViewChange('notes')}
        >
          <svg className="nav-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            <polyline points="9 22 9 12 15 12 15 22"></polyline>
          </svg>
          <span>{t.nav.home}</span>
        </button>
        
        <button 
          className={`nav-item ${currentView === 'documents' ? 'active' : ''}`}
          onClick={() => onViewChange('documents')}
        >
          <svg className="nav-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
          </svg>
          <span>{t.nav.documents}</span>
        </button>
        
        {/* Visualize merged into Discover: the note-seeded graph now lives
            there, so the old tag-seeded view has no separate nav entry. */}
        <button
          className={`nav-item ${currentView === 'discover' ? 'active' : ''}`}
          onClick={() => onViewChange('discover')}
        >
          <svg className="nav-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="2"></circle>
            <circle cx="12" cy="5" r="2"></circle>
            <circle cx="12" cy="19" r="2"></circle>
            <circle cx="5" cy="12" r="2"></circle>
            <circle cx="19" cy="12" r="2"></circle>
            <line x1="12" y1="7" x2="12" y2="10"></line>
            <line x1="12" y1="14" x2="12" y2="17"></line>
            <line x1="7" y1="12" x2="10" y2="12"></line>
            <line x1="14" y1="12" x2="17" y2="12"></line>
          </svg>
          <span>{t.nav.discover}</span>
        </button>

        <button
          className={`nav-item ${currentView === 'storyboards' ? 'active' : ''}`}
          onClick={() => onViewChange('storyboards')}
        >
          <svg className="nav-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7"></rect>
            <rect x="14" y="3" width="7" height="7"></rect>
            <rect x="14" y="14" width="7" height="7"></rect>
            <rect x="3" y="14" width="7" height="7"></rect>
          </svg>
          <span>{t.nav.storyboardsTab}</span>
        </button>

        {/* Other people's notes, documents and boards. They are deliberately
            absent from the lists above, so this is the way to them. */}
        <button
          className={`nav-item ${currentView === 'shared' ? 'active' : ''}`}
          onClick={() => onViewChange('shared')}
        >
          <svg className="nav-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
          <span>{t.nav.shared}</span>
        </button>
        
        <button 
          className={`nav-item ${currentView === 'tags' ? 'active' : ''}`}
          onClick={() => onViewChange('tags')}
        >
          <svg className="nav-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
            <line x1="7" y1="7" x2="7.01" y2="7"></line>
          </svg>
          <span>{t.nav.tagManagement}</span>
        </button>
        
        <button 
          className={`nav-item ${currentView === 'settings' ? 'active' : ''}`}
          onClick={() => onViewChange('settings')}
        >
          <svg className="nav-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"></path>
          </svg>
          <span>{t.nav.settings}</span>
        </button>
        
        <button 
          className={`nav-item ${currentView === 'about' ? 'active' : ''}`}
          onClick={() => onViewChange('about')}
        >
          <svg className="nav-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
          <span>{t.nav.about}</span>
        </button>

        <button
          className={`nav-item ${currentView === 'feedback' ? 'active' : ''}`}
          onClick={() => onViewChange('feedback')}
        >
          <svg className="nav-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
          <span>{t.nav.feedback}</span>
        </button>
      </nav>
      
      {currentView !== 'settings' && currentView !== 'discover' && currentView !== 'tags' && currentView !== 'about' && currentView !== 'storyboards' && currentView !== 'shared' && (
        <div className="sidebar-actions">
          {!isPremium && (
            <a className="upgrade-sidebar-btn" href="/pricing">
              <svg className="upgrade-icon" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                <path d="M12 2l2.9 6.26L21.5 9.27l-4.75 4.14 1.4 6.59L12 16.9 5.85 20l1.4-6.59L2.5 9.27l6.6-1.01z"></path>
              </svg>
              {t.nav.upgrade}
            </a>
          )}
          {currentView === 'documents' && onImportClick && (
            <button className="import-btn" onClick={onImportClick}>
              <svg className="import-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              {t.nav.importDocuments}
            </button>
          )}
          
          <button className="add-note-btn" onClick={onAddNoteClick}>
            <svg className="add-note-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {currentView === 'documents' ? (
                <>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="12" y1="18" x2="12" y2="12"></line>
                  <line x1="9" y1="15" x2="15" y2="15"></line>
                </>
              ) : (
                <>
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </>
              )}
            </svg>
            {currentView === 'documents' ? t.nav.addDocument : t.nav.addNote}
          </button>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
